import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit24Regular } from '@fluentui/react-icons';
import { useApi } from '../auth/useApi.ts';
import { useI18n } from '../i18n/context.tsx';

interface ParentKeyRow {
  id: string;
  label: string;
  accessKey: string;
  groupId: string | null;
  maxRps: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  form: { display: 'grid', gap: '12px', minWidth: '420px' },
  spinnerWrap: { padding: '40px' },
});

export function ParentKeysPage() {
  const styles = useStyles();
  const qc = useQueryClient();
  const { request } = useApi();
  const { t } = useI18n();
  const parents = useQuery({
    queryKey: ['parentKeys'],
    queryFn: () => request<ParentKeyRow[]>('/admin/parent-keys'),
  });
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ParentKeyRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ParentKeyRow | null>(null);

  const toggleEnabled = useMutation({
    mutationFn: (k: ParentKeyRow) =>
      request(`/admin/parent-keys/${k.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !k.enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parentKeys'] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => request(`/admin/parent-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parentKeys'] }),
  });

  return (
    <div>
      <div className={styles.header}>
        <Title2>{t('parentKeys', 'title')}</Title2>
        <Button appearance="primary" icon={<Add24Regular />} onClick={() => setOpen(true)}>
          {t('common', 'add')}
        </Button>
      </div>

      <Card>
        {parents.isLoading ? (
          <div className={styles.spinnerWrap}><Spinner /></div>
        ) : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('common', 'label')}</TableHeaderCell>
                <TableHeaderCell>{t('parentKeys', 'accessKey')}</TableHeaderCell>
                <TableHeaderCell>Group ID</TableHeaderCell>
                <TableHeaderCell>{t('parentKeys', 'maxRps')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'enabled')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'createdAt')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'actions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parents.data?.map((k) => (
                <TableRow key={k.id}>
                  <TableCell>{k.label}</TableCell>
                  <TableCell><span className={styles.mono}>{k.accessKey}</span></TableCell>
                  <TableCell><span className={styles.mono}>{k.groupId ?? '—'}</span></TableCell>
                  <TableCell>{k.maxRps}/s</TableCell>
                  <TableCell>
                    <Switch checked={k.enabled} onChange={() => toggleEnabled.mutate(k)} />
                  </TableCell>
                  <TableCell>{new Date(k.createdAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Edit24Regular />}
                      onClick={() => setEditTarget(k)}
                    />
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Delete24Regular />}
                      onClick={() => setDeleteTarget(k)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateParentKeyDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['parentKeys'] })}
      />

      <EditParentKeyDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['parentKeys'] })}
      />

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('common', 'confirmDeleteTitle')}</DialogTitle>
            <DialogContent>
              {deleteTarget
                ? t('parentKeys', 'confirmDelete', { label: deleteTarget.label })
                : null}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common', 'cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Delete24Regular />}
                onClick={() => {
                  if (deleteTarget) del.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }}
              >
                {t('common', 'delete')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

function CreateParentKeyDialog({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const styles = useStyles();
  const { request } = useApi();
  const { t } = useI18n();
  const [label, setLabel] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [groupId, setGroupId] = useState('');
  const [maxRps, setMaxRps] = useState(5);

  const create = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { label, accessKey, secretKey, maxRps };
      if (groupId.trim()) body.groupId = groupId.trim();
      return request('/admin/parent-keys', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      onCreated();
      onClose();
      setLabel(''); setAccessKey(''); setSecretKey(''); setGroupId(''); setMaxRps(5);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('parentKeys', 'createTitle')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t('parentKeys', 'labelHelp')}>
                <Input value={label} onChange={(_, d) => setLabel(d.value)} />
              </Field>
              <Field label={t('parentKeys', 'accessKeyHelp')}>
                <Input value={accessKey} onChange={(_, d) => setAccessKey(d.value)} />
              </Field>
              <Field label={t('parentKeys', 'secretKeyHelp')}>
                <Input type="password" value={secretKey} onChange={(_, d) => setSecretKey(d.value)} />
              </Field>
              <Field label="Group ID (optional)">
                <Input
                  value={groupId}
                  placeholder="G98758157255"
                  onChange={(_, d) => setGroupId(d.value)}
                />
              </Field>
              <Field label={t('parentKeys', 'maxRpsHelp')}>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={String(maxRps)}
                  onChange={(_, d) => setMaxRps(Number(d.value) || 1)}
                />
              </Field>
              {create.isError ? (
                <MessageBar intent="error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('common', 'createFailed')}</MessageBarTitle>
                    {(create.error as Error)?.message ?? String(create.error)}
                  </MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>{t('common', 'cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              disabled={!label || !accessKey || !secretKey || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common', 'create')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function EditParentKeyDialog({
  target, onClose, onSaved,
}: {
  target: ParentKeyRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const styles = useStyles();
  const { request } = useApi();
  const { t } = useI18n();
  const [label, setLabel] = useState('');
  const [groupId, setGroupId] = useState<string>('');
  const [maxRps, setMaxRps] = useState(5);

  // Reset local state whenever the dialog re-opens against a different row.
  const targetId = target?.id ?? null;
  useEffect(() => {
    if (target) {
      setLabel(target.label);
      setGroupId(target.groupId ?? '');
      setMaxRps(target.maxRps);
    }
  }, [targetId]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { label, maxRps };
      // Send null to clear, or the trimmed value to set.
      body.groupId = groupId.trim() || null;
      return request(`/admin/parent-keys/${target!.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open={target !== null} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('parentKeys', 'editTitle')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t('parentKeys', 'labelHelp')}>
                <Input value={label} onChange={(_, d) => setLabel(d.value)} />
              </Field>
              <Field label="Group ID (optional)">
                <Input
                  value={groupId}
                  placeholder="G98758157255"
                  onChange={(_, d) => setGroupId(d.value)}
                />
              </Field>
              <Field label={t('parentKeys', 'maxRpsHelp')}>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={String(maxRps)}
                  onChange={(_, d) => setMaxRps(Number(d.value) || 1)}
                />
              </Field>
              {save.isError ? (
                <MessageBar intent="error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('common', 'saveFailed')}</MessageBarTitle>
                    {(save.error as Error)?.message ?? String(save.error)}
                  </MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>{t('common', 'cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              disabled={!label || save.isPending}
              onClick={() => save.mutate()}
            >
              {t('parentKeys', 'save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
